import api from '../../api';
import {
	SECTION_ADDITIONAL,
	SECTION_COMPETITION,
	SECTION_CUSTOM_EVENT,
	SECTION_CUSTOM_PARTICIPANT,
	SECTION_DEFAULT, SECTION_MARKING_ATHLETE,
	SECTION_MARKING_EVENT, SECTION_RUN,
	SECTION_SCOREBOARD,
} from '@videotools/js/api/entities/TagGroup';

const KEY_LOCAL_STORAGE_SELECTED_TAG_GROUPS = 'tagging.selected_tag_groups';
const TAG_GROUP_TYPE_TIMING = 'timing';

const normalizeStorage = (localTagGroups) => {
	if (!localTagGroups || typeof localTagGroups !== 'object') {
		return {};
	}
	const isGroupedBySport = Object.keys(localTagGroups).every(key => !isNaN(key));
	if (!isGroupedBySport) {
		return {};
	}
	return localTagGroups;
};

export const getStoredTagGroups = () => {
	const str = localStorage.getItem(KEY_LOCAL_STORAGE_SELECTED_TAG_GROUPS);
	if (!str || str === 'undefined') {
		return {};
	}
	const storageSelectedTagGroups = JSON.parse(str);
	return normalizeStorage(storageSelectedTagGroups);
};

export const initialState = () => {
	return {
		tagGroups:        [],
		selectedTagGroup: null,
	};
};

export const mutations = {
	setTagGroups(state, tagGroups) {
		state.tagGroups = tagGroups;
	},
	setSelected(state, selectedTagGroup) {
		state.selectedTagGroup = selectedTagGroup;
	},
	setDefault(state, tagGroupId) {
		state.tagGroups = state.tagGroups.map(item => {
			item.default = item.id === tagGroupId;
			return item;
		});
	},
};

export const actions = {
	async init({ dispatch, getters, rootGetters }) {
		await dispatch('fetchTagGroups');
		const sportId = rootGetters['event/sportId'];
		const storedTagGroups = getStoredTagGroups();
		if (storedTagGroups[sportId]) {
			const tagGroup = await dispatch('getById', storedTagGroups[sportId]);
			if (tagGroup) {
				await dispatch('setSelected', tagGroup);
				return;
			}
		}
		await dispatch('setSelected', getters.defaultTagGroup);
	},
	async getById({ getters }, id) {
		return getters.tagGroups.find(tg => tg.id === id);
	},
	async setSelected({ commit, rootGetters, dispatch }, tagGroup) {
		const event = rootGetters['event/event'];
		const sportId = rootGetters['event/sportId'];
		await commit('setSelected', tagGroup);
		const storedTagGroups = getStoredTagGroups();
		storedTagGroups[sportId] = tagGroup.id;
		localStorage.setItem(KEY_LOCAL_STORAGE_SELECTED_TAG_GROUPS, JSON.stringify(storedTagGroups));
		dispatch('event/loadEventTags', { id: event.id }, { root: true });
		dispatch('sync/registerEventTagsListener', event.id, { root: true });
	},
	async fetchTagGroups({ commit, getters, rootGetters }) {
		const event = rootGetters['event/event'];
		const tagGroups = await api.getTagGroups(event.id);
		commit('setTagGroups', tagGroups);
		if (getters.selectedTagGroup) {
			commit('setSelected', tagGroups.find(tg => tg.id === getters.selectedTagGroup.id));
		}
	},
	/**
	 * @param {Object} tagGroupData { name, visibility, sections: array }
	 * @param {Array} customButtons [<TagButton>]
	 * @return {Promise<*>}
	 */
	async create({ dispatch, rootGetters }, { tagGroupData, customButtons }) {
		const sportId = rootGetters['event/sportId'];
		const data = await api.createTagGroup(sportId, tagGroupData);
		await api.createCustomButtons(data.id, customButtons);
		await dispatch('fetchTagGroups');

		return data;
	},
	async update({ dispatch, getters }, { tagGroupId, tagGroupData, customButtons }) {
		if (getters.defaultTagGroup.id !== tagGroupId) {
			// only call update for non system default tag groups
			await api.updateTagGroup(tagGroupId, tagGroupData);
		}
		if (typeof customButtons === 'object') {
			await api.updateCustomButtons(tagGroupId, customButtons);
		}
		dispatch('fetchTagGroups');
	},
	async duplicate({ dispatch }, tagGroup) {
		const name = `${tagGroup.name} Copy`;
		const data = {
			tagGroupData: {
				name,
				visibility: tagGroup.visibility,
				sections:   [...tagGroup.sections],
			},
			customButtons: [...tagGroup.customButtons],
		};
		const { id } = await dispatch('create', data);

		await dispatch('fetchTagGroups');
		const createdTagGroup = await dispatch('getById', id);
		dispatch('setSelected', createdTagGroup);
	},
	async delete({ dispatch, getters, commit }, tagGroupId) {
		let idx;
		if (getters.selectedTagGroup &&
			getters.selectedTagGroup.id === tagGroupId
		) {
			idx = getters.tagGroups.indexOf(getters.selectedTagGroup);
		}
		await api.deleteTagGroup(tagGroupId);
		commit('setTagGroups', getters.tagGroups.filter(tg => tg.id !== tagGroupId));
		if (idx) {
			dispatch('setSelected', getters.tagGroups[idx - 1]);
		}
	},
};

export const getters = {
	/**
	 * @return {[TagGroup]}
	 */
	tagGroups: (state, getters) => {
		if (!state.tagGroups?.length) {
			return [];
		}
		return [
			getters.defaultTagGroup,
			...state.tagGroups.filter(tg => tg.id !== getters.defaultTagGroup?.id && tg.type !== 'timing'),
		];
	},
	selectedTagGroup: (state) => {
		return state.selectedTagGroup;
	},
	storedTagGroupId: (state, getters, rootState, rootGetters) => {
		const storedTagGroups = getStoredTagGroups();
		const sportId = rootGetters['event/sportId'];
		return storedTagGroups[sportId];
	},
	defaultTagGroup: (state) => {
		return state.tagGroups.find(tg => tg.isSystemDefault);
	},
	defaultSectionButtons: (state, getters) => {
		return getters.defaultTagGroup.defaultButtons;
	},
	timingTagGroup: (state) => {
		return state.tagGroups.find(tg => tg.type === TAG_GROUP_TYPE_TIMING);
	},
	additionalSectionButtons: (state, getters) => {
		return getters.defaultTagGroup.additionalParticipantButtons;
	},
	createdTagGroups: (state) => {
		return state.tagGroups.filter(tg => tg.isDefault && !tg.isPublic);
	},
	tags: (state, getters) => {
		if (getters.selectedTagGroup.id === getters.defaultTagGroup.id) {
			return getters.defaultTagGroup.tagButtons;
		}
		return [...getters.defaultTagGroup.tagButtons, ...getters.selectedTagGroup.tagButtons];
	},
	timingTags: (state, getters) => {
		return getters.timingTagGroup.tagButtons;
	},
	availableSections: (state, getters) => {
		// all available sections are contained within the system default tag group
		return getters.defaultTagGroup?.availableSections || [];
	},
	tagGroupSectionStatuses: (state, getters, _) => (tagGroup) => {
		return getters.availableSections.reduce((acc, section) => {
			let enabled = tagGroup.sections?.includes(section);

			if (SECTION_CUSTOM_EVENT === section) {
				if (tagGroup.customEventButtons.length > 0) {
					// enable custom section for default group only if it contains buttons
					enabled = tagGroup.isSystemDefault ? true : enabled;
				} else {
					// disable if section doesn't contain any buttons
					enabled = false;
				}
			} else if (SECTION_CUSTOM_PARTICIPANT === section) {
				if (tagGroup.customParticipantButtons.length > 0) {
					enabled = tagGroup.isSystemDefault ? true : enabled;
				} else {
					// disable if section doesn't contain any buttons
					enabled = false;
				}
			} else if (SECTION_DEFAULT === section && !getters.defaultSectionButtons.length) {
				enabled = false;
			}

			return { ...acc, [section]: enabled };
		}, {});
	},
	eventSections: (state, getters, rootState, rootGetters) => {
		// TODO: the event sections information should be provided by the API instead of hardcoded here
		const isSequential = rootGetters['event/isSequentialCompetition'];
		const availableSections = getters.availableSections;
		if (isSequential) {
			return [
				SECTION_COMPETITION,
				SECTION_CUSTOM_EVENT,
				SECTION_MARKING_EVENT,
			].filter(section => availableSections.includes(section));
		}
		return [
			SECTION_MARKING_EVENT,
			SECTION_COMPETITION,
			SECTION_SCOREBOARD,
			SECTION_CUSTOM_EVENT,
		].filter(section => availableSections.includes(section));
	},
	participantSections: (state, getters, rootState, rootGetters) => {
		// TODO: the participant sections information should be provided by the API instead of hardcoded here
		const isSequential = rootGetters['event/isSequentialCompetition'];
		const availableSections = getters.availableSections;
		if (isSequential) {
			return [
				SECTION_RUN,
				SECTION_ADDITIONAL,
				SECTION_CUSTOM_PARTICIPANT,
				SECTION_MARKING_ATHLETE,
			].filter(section => availableSections.includes(section));
		}
		return [
			SECTION_DEFAULT,
			SECTION_ADDITIONAL,
			SECTION_CUSTOM_PARTICIPANT,
		].filter(section => availableSections.includes(section));
	},
};

export const tagGroups = {
	namespaced: true,
	state:      initialState(),
	actions,
	mutations,
	getters,
};

export default tagGroups;
